import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations } from "@/lib/i18n";
import { getSettings } from "@/lib/api";

const AppContext = createContext(null);

const LS_LANG = "ksk_lang";
const LS_FARMER = "ksk_farmer";
const LS_CART = "ksk_cart";
const LS_ADMIN = "ksk_admin_token";

export const AppProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(LS_LANG) || "hi");
  const [farmer, setFarmerState] = useState(() => {
    const raw = localStorage.getItem(LS_FARMER);
    return raw ? JSON.parse(raw) : null;
  });
  const [cart, setCartState] = useState(() => {
    const raw = localStorage.getItem(LS_CART);
    return raw ? JSON.parse(raw) : [];
  });
  const [adminToken, setAdminTokenState] = useState(() => localStorage.getItem(LS_ADMIN) || null);
  const [shopSettings, setShopSettings] = useState(null);

  useEffect(() => {
    getSettings().then(setShopSettings).catch(() => {});
  }, []);

  useEffect(() => { localStorage.setItem(LS_LANG, lang); }, [lang]);
  useEffect(() => { localStorage.setItem(LS_CART, JSON.stringify(cart)); }, [cart]);

  const t = useCallback((key, params) => {
    let str = translations[lang]?.[key] ?? translations.en[key] ?? key;
    if (params) {
      Object.keys(params).forEach((k) => {
        str = str.replace(`{${k}}`, params[k]);
      });
    }
    return str;
  }, [lang]);

  const setLang = (l) => setLangState(l);
  const toggleLang = () => setLangState((prev) => (prev === "hi" ? "en" : "hi"));

  const setFarmer = (f) => {
    setFarmerState(f);
    if (f) localStorage.setItem(LS_FARMER, JSON.stringify(f));
    else localStorage.removeItem(LS_FARMER);
  };

  const setAdminToken = (tok) => {
    setAdminTokenState(tok);
    if (tok) localStorage.setItem(LS_ADMIN, tok);
    else localStorage.removeItem(LS_ADMIN);
  };

  const addToCart = (product, quantity = 1) => {
    setCartState((prev) => {
      const idx = prev.findIndex((it) => it.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, {
        product_id: product.id,
        name_en: product.name_en,
        name_hi: product.name_hi,
        price: product.price,
        quantity,
        unit: product.unit,
        image_path: product.image_path,
      }];
    });
  };

  const updateCartQty = (productId, quantity) => {
    setCartState((prev) => prev.map((it) => it.product_id === productId ? { ...it, quantity } : it).filter((it) => it.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCartState((prev) => prev.filter((it) => it.product_id !== productId));
  };

  const clearCart = () => setCartState([]);

  const reorderItems = (items) => {
    // Merge past order items into current cart
    setCartState((prev) => {
      const next = [...prev];
      items.forEach((it) => {
        const idx = next.findIndex((x) => x.product_id === it.product_id);
        if (idx >= 0) {
          next[idx] = { ...next[idx], quantity: next[idx].quantity + it.quantity };
        } else {
          next.push({
            product_id: it.product_id,
            name_en: it.name_en,
            name_hi: it.name_hi,
            price: it.price,
            quantity: it.quantity,
            unit: it.unit,
            image_path: it.image_path,
          });
        }
      });
      return next;
    });
  };

  const cartCount = cart.reduce((s, it) => s + it.quantity, 0);
  const cartSubtotal = cart.reduce((s, it) => s + it.price * it.quantity, 0);

  // Delivery fee from shop settings (default: ₹40 if under ₹500)
  const feeAmount = Number(shopSettings?.delivery_fee ?? 40);
  const threshold = Number(shopSettings?.free_delivery_threshold ?? 500);
  let deliveryFee = 0;
  if (cart.length > 0) {
    if (feeAmount <= 0) {
      deliveryFee = 0;
    } else if (threshold > 0 && cartSubtotal >= threshold) {
      deliveryFee = 0;
    } else {
      deliveryFee = feeAmount;
    }
  }
  const cartTotal = cartSubtotal + deliveryFee;

  const value = {
    lang, setLang, toggleLang, t,
    farmer, setFarmer,
    adminToken, setAdminToken,
    cart, addToCart, updateCartQty, removeFromCart, clearCart, reorderItems,
    cartCount, cartSubtotal, deliveryFee, cartTotal,
    shopSettings, feeAmount, threshold,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
